/* eslint-disable @nx/enforce-module-boundaries */
import { useMemo, useState, useEffect } from 'react';
import { useIndustry } from 'v4/lib/hooks/use-industry';
import { CustomDropdown } from '../custom-dropdown';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

export const IndustryDropdown = ({
  dropDownAerrow, 
  placeHolder, 
  onChange, 
  value, 
  defaultValue, 
  clearOnSelect = false,
  keyName = 'name', // Add keyName prop to know what field to use for selection
  ...props 
}) => {
  // Initialize with value or defaultValue
  const initialValue = value || defaultValue || '';
  const [internalValue, setInternalValue] = useState(initialValue);
  const { t } = useTranslator();
  
  const { data, refetch, createLoading, mutateAsync, isLoading } = useIndustry({
    search: internalValue,
  });

  // Update internal value when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value || '');
    }
  }, [value]);

  // Set initial value when defaultValue is provided and no value prop
  useEffect(() => {
    if (defaultValue && value === undefined) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, value]);

  const industries = useMemo(
    () => data?.pages?.reduceRight?.((ac, p) => ac.concat(p?.results), []),
    [data?.pages]
  );

  // Function to get display value (name) from ID
  const getDisplayValue = (val) => {
    if (!val || !industries?.length) return val || '';
    
    // If keyName is 'id', find the industry name by ID
    if (keyName === 'id') {
      const industry = industries.find(ind => ind.id === val);
      return industry?.name || '';
    }
    
    // Otherwise, return the value as is (it's already a name)
    return val;
  };

  // Function to get the actual value to display in input
  const getInputDisplayValue = () => {
    if (keyName === 'id') {
      return getDisplayValue(value) || getDisplayValue(defaultValue) || internalValue;
    }
    return internalValue;
  };

  const handleCreateIndustry = async (industryName) => {
    try {
      // const resp = await mutateAsync({ name: industryName });
      
      // if (resp) {
        const newIndustry = { name: industryName };
        if (clearOnSelect) {
          setInternalValue('');
        } else {
          setInternalValue(industryName);
        }
        onChange?.(newIndustry);
      // }
    } catch (e) {
      console.error('Error creating industry:', e);
    }
  };

  const handleSearch = (searchValue) => {
    setInternalValue(searchValue);
  };

  const handleChange = (selectedItem) => {
    const industryName = selectedItem?.name || selectedItem;
    
    if (clearOnSelect) {
      setInternalValue(''); // Clear the input after selection
    } else {
      // Show the name in the input field regardless of what keyName is used for selection
      setInternalValue(industryName);
    }
    
    onChange?.(selectedItem);
  };

  return (
    <CustomDropdown
      {...props}
      allowCreate={true}
      value={getInputDisplayValue()} // Use the display value function
      placeHolder={placeHolder}
      data={industries}
      isLoading={isLoading}
      createLoading={createLoading}
      dropDownAerrow={dropDownAerrow}
      onSearch={handleSearch}
      onChange={handleChange}
      onCreateItem={handleCreateIndustry}
      clearOnSelect={clearOnSelect}
      keyName={keyName}
    />
  );
};
